import { useState } from 'react'
import { Search, X, Loader2 } from 'lucide-react'
import { useUserSearch } from '@/hooks/useUserSearch'
import { Avatar } from '@/components/ui/Avatar'
import { User } from '@/types/user'

interface MemberSearchProps {
  onSelect: (user: User) => void
  placeholder?: string
}

export default function MemberSearch({ onSelect, placeholder = 'Search by name...' }: MemberSearchProps) {
  const [query, setQuery] = useState('')
  const { results, isLoading, error } = useUserSearch(query)

  const showDropdown = query.trim().length > 0

  const handleSelect = (user: User) => {
    onSelect(user)
    setQuery('')
  }

  return (
    <div className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
        <input
          type="text"
          role="textbox"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={placeholder}
          className="w-full pl-9 pr-9 py-2 border border-gray-300 rounded-lg text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        />
        {query && (
          <button
            type="button"
            aria-label="Clear search"
            onClick={() => setQuery('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {showDropdown && (
        <div className="absolute z-10 mt-2 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
          {isLoading && (
            <div className="flex items-center gap-2 px-4 py-3 text-sm text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin" />
              Searching...
            </div>
          )}

          {error && (
            <p role="alert" className="px-4 py-3 text-sm text-red-600">
              {error}
            </p>
          )}

          {!isLoading && !error && results.length === 0 && (
            <p className="px-4 py-3 text-sm text-gray-500">No users found</p>
          )}

          {!isLoading && !error && results.length > 0 && (
            <ul>
              {results.map((user) => (
                <li key={user.id}>
                  <button
                    type="button"
                    onClick={() => handleSelect(user)}
                    className="w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-gray-50"
                  >
                    <Avatar src={user.profile?.profilePicture} name={`${user.firstName} ${user.lastName}`} size="sm" />
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-gray-900 truncate">
                        {user.firstName} {user.lastName}
                      </span>
                      <span className="block text-xs text-gray-500 truncate">{user.email}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}
